<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("MACHINE_SERVICE_SIGNALS.TITLE") }}
            <i class="mr-2"></i>
            <small>{{ $t("MACHINE_SERVICE_SIGNALS.LIST") }}</small>
          </h3>
        </div>
      </template>

      <v-card v-show="!isPageLoading">
        <ResponsiveFilterSheet
          :filters.sync="filters"
          :searchQuery.sync="searchQuery"
          @search="doSearch"
          @clear="doClear"
        >
          <template v-slot:desktop>
            <SearchTools
              :searchQuery.sync="searchQuery"
              hideActions
              @do-search="doSearch"
              @do-clear="doClear"
            >
              <template v-slot:prepend>
                <b-col class="pb-0" :md="4">
                  <b-form-group
                    id="machine-service-signals-open-group"
                    label-for="machine-service-signals-open-input"
                  >
                    <FormSelect
                      id="machine-service-signals-open-input"
                      :placeholder="'Отворени'"
                      :options="openFilterOptions"
                      clearable
                      :value="filters.open"
                      @input="updateOpenFilter"
                    />
                  </b-form-group>
                </b-col>
                <b-col class="pb-0 d-none d-md-block" :md="8"></b-col>
              </template>
            </SearchTools>
          </template>
          <template v-slot:mobile-fields="{ filters: draftFilters, updateFilter }">
            <FormSelect
              id="machine-service-signals-open-input-mobile"
              :placeholder="'Отворени'"
              :options="openFilterOptions"
              clearable
              :value="draftFilters.open"
              @input="updateFilter('open', $event)"
            />
          </template>
        </ResponsiveFilterSheet>

        <ResponsiveDataTable
          :headers="headers"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isLoadingMachineServiceSignal"
          :loading-text="$t('MACHINE_SERVICE_SIGNALS.LOADING')"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          class="elevation-1"
          @update:options="getDataFromApi"
          :mobile-summary-fields="mobileSummaryFields"
          :mobile-detail-fields="mobileDetailFields"
          :mobile-toggle-label="$t('LABELS.DETAILS')"
        >
          <template v-slot:[`item.description`]="{ item }">
            <span class="signal-description">{{ item.description }}</span>
          </template>

          <template v-slot:[`item.machine`]="{ item }">
            {{ formatMachineName(item.machine) }}
          </template>

          <template v-slot:[`item.staff`]="{ item }">
            {{ item.staff ? item.staff.name : "-" }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <router-link :to="serviceVisitCreateRoute(item)">
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-permission="['service-visits-create']"
                    class="mr-2"
                    color="success"
                    fab
                    dark
                    x-small
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Създай сервизно посещение</span>
              </v-tooltip>
            </router-link>
            <router-link
              :to="{ name: 'show-machine-service-signals', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mr-2" fab dark x-small v-bind="attrs" v-on="on">
                    <v-icon dark>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("MACHINE_SERVICE_SIGNALS.SHOW") }}</span>
              </v-tooltip>
            </router-link>
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-permission="['machine-service-signals-delete']"
                  color="error"
                  fab
                  dark
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  @click="onDelete(item.id)"
                >
                  <v-icon dark>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("BUTTON.DELETE") }}</span>
            </v-tooltip>
          </template>
          <template v-slot:mobile-summary-actions="{ item }">
            <router-link :to="serviceVisitCreateRoute(item)">
              <v-btn
                v-permission="['service-visits-create']"
                color="success"
                aria-label="Създай сервизно посещение"
                fab
                dark
                x-small
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </router-link>
            <router-link
              :to="{ name: 'show-machine-service-signals', params: { id: item.id } }"
            >
              <v-btn
                :aria-label="$t('MACHINE_SERVICE_SIGNALS.SHOW')"
                dark
                fab
                x-small
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </router-link>
            <v-btn
              v-permission="['machine-service-signals-delete']"
              color="error"
              :aria-label="$t('BUTTON.DELETE')"
              fab
              dark
              x-small
              @click="onDelete(item.id)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </ResponsiveDataTable>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import ResponsiveDataTable from "@/core/components/table/ResponsiveDataTable.vue";
import ResponsiveFilterSheet from "@/core/components/table/ResponsiveFilterSheet.vue";
import FormSelect from "@/view/content/forms/components/FormSelect.vue";
import {
  DELETE_MACHINE_SERVICE_SIGNAL,
  FETCH_MACHINE_SERVICE_SIGNALS
} from "@/modules/machine-service-signals/store/machine-service-signals.module";

export default {
  components: {
    FormSelect,
    SearchTools,
    ResponsiveDataTable,
    ResponsiveFilterSheet
  },
  data() {
    return {
      isPageLoading: true,
      items: [],
      totalItems: 100,
      searchQuery: "",
      options: {
        sortBy: ["dateTime"],
        sortDesc: [true],
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      filters: {
        open: true
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      headers: [
        { text: this.$i18n.t("MACHINE_SERVICE_SIGNALS.DATE_TIME"), value: "dateTime" },
        { text: this.$i18n.t("MACHINE_SERVICE_SIGNALS.MACHINE"), value: "machine" },
        { text: this.$i18n.t("MACHINE_SERVICE_SIGNALS.STAFF"), value: "staff" },
        { text: this.$i18n.t("MACHINE_SERVICE_SIGNALS.DESCRIPTION"), value: "description" },
        {
          align: "right",
          text: this.$i18n.t("MENU.ITEM.ACTIONS"),
          value: "actions",
          sortable: false,
          width: "180px"
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["isLoadingMachineServiceSignal"]),
    params() {
      return {
        ...this.options,
        ...this.filters,
        query: this.searchQuery
      };
    },
    openFilterOptions() {
      return [
        { id: true, name: "Отворени" },
        { id: false, name: "Свързани" }
      ];
    },
    mobileSummaryFields() {
      return [
        {
          key: "machine",
          label: this.$t("MACHINE_SERVICE_SIGNALS.MACHINE"),
          primary: true,
          value: item => this.formatMachineName(item.machine) || "-"
        },
        {
          key: "dateTime",
          label: this.$t("MACHINE_SERVICE_SIGNALS.DATE_TIME")
        },
        {
          key: "staff",
          label: this.$t("MACHINE_SERVICE_SIGNALS.STAFF"),
          value: item => item.staff ? item.staff.name : "-"
        }
      ];
    },
    mobileDetailFields() {
      return [
        {
          key: "description",
          label: this.$t("MACHINE_SERVICE_SIGNALS.DESCRIPTION")
        }
      ];
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MACHINE_SERVICE_SIGNALS.TITLE"),
        route: { name: "list-machine-service-signals" }
      },
      { title: this.$i18n.t("MACHINE_SERVICE_SIGNALS.LIST") }
    ]);
  },
  methods: {
    doSearch() {
      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    doClear() {
      this.searchQuery = "";
      this.filters = { ...this.filters, open: null };
      this.doSearch();
    },
    updateOpenFilter(open) {
      this.filters = {
        ...this.filters,
        open: open === undefined ? null : open
      };
      this.doSearch();
    },
    getDataFromApi() {
      let params = this.params;
      params.length = params.itemsPerPage;
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1);
      let apiParams = this.$url.transformParams(params);

      this.$store
        .dispatch(FETCH_MACHINE_SERVICE_SIGNALS, apiParams)
        .then(data => {
          this.items = data.data.items;
          this.totalItems = data.data.totalItemsCount;
          this.isPageLoading = false;
        });
    },
    formatMachineName(machine) {
      if (!machine) {
        return "";
      }

      if (machine.resource) {
        return machine.resource.name || machine.resource.serviceDisplayName || "";
      }

      return typeof machine === "string" ? machine : machine.name;
    },
    serviceVisitCreateRoute(item) {
      return {
        name: "add-service-visits",
        query: {
          machineId: item.machineId,
          machineServiceSignalId: item.id
        }
      };
    },
    onDelete(id) {
      this.$dialog.confirm(this.$t("BUTTON.PLEASE_CONFIRM")).then(() => {
        this.$store
          .dispatch(DELETE_MACHINE_SERVICE_SIGNAL, id)
          .then(data => {
            this.$notify({
              group: "notify",
              type: "success",
              title: "<i class='flaticon2-checkmark'></i> Success",
              text: data.message
            });
            this.getDataFromApi();
          })
          .catch(response => {
            this.$notify({
              group: "notify",
              type: "error",
              title: this.$t("ALERTS.ERROR"),
              text: response.data.message
            });
          });
      });
    }
  }
};
</script>

<style scoped>
.signal-description {
  display: inline-block;
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
